import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Camera, Heart, Images, Menu, Search, ShoppingBag, X } from "lucide-react";
import { useCatalog } from "../../context/CatalogContext";
import { api } from "../../lib/api";
const CustomerImageSearch = lazy(() => import("../CustomerImageSearch"));

const NAV_ITEMS = [
  {
    to: "/",
    label: "Home",
    testid: "nav-home",
    end: true,
    eyebrow: "Start here",
    title: "A quick view of Samrat Glass",
    description: "Featured lighting, spaces, heritage, atelier pieces and real installations in one editorial overview.",
    links: [["/collections", "Collections"], ["/spaces", "Shop by Space"], ["/gallery", "Installations"]],
  },
  {
    to: "/catalog",
    label: "Catalog",
    testid: "nav-catalog",
    eyebrow: "Browse lighting",
    title: "Explore the complete lighting range",
    description: "Browse chandeliers, hanging lights, wall lights, lamps and the wider catalogue by category, price or search.",
    links: [["/catalog", "Full Catalog"], ["/spaces", "By Space"], ["/custom-lighting-bulk-orders", "Custom Lighting"]],
  },
  {
    to: "/collections",
    label: "Collections",
    testid: "nav-collections",
    eyebrow: "Curated families",
    title: "Lighting grouped by design language",
    description: "Discover related pieces and coordinated families without searching through the full catalogue.",
    links: [["/collections", "All Collections"], ["/catalog", "All Products"], ["/styled-by", "Styled By"]],
  },
  {
    to: "/craft",
    label: "The Craft",
    testid: "nav-craft",
    eyebrow: "Made in Firozabad",
    title: "Glassmaking, finishing and custom work",
    description: "See the craftsmanship behind the fixtures, the manufacturing story and how custom lighting is developed.",
    links: [["/craft", "The Craft"], ["/chandelier-manufacturer-india", "Manufacturing"], ["/custom-lighting-bulk-orders", "Custom Orders"]],
  },
  {
    to: "/gallery",
    label: "Gallery",
    testid: "nav-gallery",
    eyebrow: "Real installations",
    title: "See Samrat Glass in finished spaces",
    description: "Browse completed residential, hospitality and project installations across India.",
    links: [["/gallery", "Project Gallery"], ["/styled-by", "Styled By"], ["/architects-interior-designers", "For Designers"]],
  },
  {
    to: "/about",
    label: "About",
    testid: "nav-about",
    eyebrow: "Since 1981",
    title: "The people and legacy behind the brand",
    description: "Learn about Samrat Glass Emporium, its Firozabad roots, founder story and four decades of decorative lighting.",
    links: [["/about", "Our Story"], ["/craft", "Our Craft"], ["/faq", "FAQ"]],
  },
  {
    to: "/contact",
    label: "Contact",
    testid: "nav-contact",
    eyebrow: "Talk to us",
    title: "Product, project and custom enquiries",
    description: "Reach the team for product questions, custom lighting, bulk requirements and architect or designer projects.",
    links: [["/contact", "Contact"], ["/custom-lighting-bulk-orders", "Custom Lighting"], ["/architects-interior-designers", "Architects & Designers"]],
  },
];

export default function Header() {
  const { cart, favorites } = useCatalog();
  const location = useLocation();
  const navigate = useNavigate();
  const searchMenuRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState("Samrat Glass Emporium");
  const [navPreview, setNavPreview] = useState(null);
  const [projectTabHost, setProjectTabHost] = useState(null);
  const [projectTabActive, setProjectTabActive] = useState(() => window.location.hash === "#project-gallery");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    api.getSettings().then((s) => setBrand(s.brand_name || "Lumière")).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setNavPreview(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!searchOpen) return undefined;
    const closeFromOutside = (event) => {
      if (event.target?.closest?.('[data-customer-image-search-overlay="true"]')) return;
      if (!searchMenuRef.current?.contains(event.target)) setSearchOpen(false);
    };
    const closeFromKeyboard = (event) => {
      if (event.key === "Escape") setSearchOpen(false);
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromKeyboard);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromKeyboard);
    };
  }, [searchOpen]);

  useEffect(() => {
    const onHashChange = () => setProjectTabActive(window.location.hash === "#project-gallery");
    window.addEventListener("hashchange", onHashChange);
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/admin") {
      setProjectTabHost(null);
      return undefined;
    }

    let host = null;
    let nav = null;
    let retryTimer = null;

    const clearProjectState = (event) => {
      const button = event.target.closest('button[data-testid^="admin-tab-"]');
      if (!button || button.dataset.testid === "admin-tab-project-gallery") return;
      if (window.location.hash === "#project-gallery") {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
        setProjectTabActive(false);
      }
    };

    const attach = () => {
      const homepageButton = document.querySelector('[data-testid="admin-tab-homepage"]');
      const nextNav = homepageButton?.parentElement;
      if (!homepageButton || !nextNav) {
        retryTimer = window.setTimeout(attach, 50);
        return;
      }

      nav = nextNav;
      const existing = nav.querySelector('[data-persistent-project-gallery-tab-host="true"]');
      host = existing || document.createElement("span");
      host.setAttribute("data-persistent-project-gallery-tab-host", "true");
      host.style.display = "contents";
      if (!existing) homepageButton.insertAdjacentElement("afterend", host);
      nav.addEventListener("click", clearProjectState);
      setProjectTabHost(host);
    };

    attach();

    return () => {
      if (retryTimer) window.clearTimeout(retryTimer);
      if (nav) nav.removeEventListener("click", clearProjectState);
      if (host?.parentNode) host.parentNode.removeChild(host);
      setProjectTabHost(null);
    };
  }, [location.pathname]);

  const openProjectGallery = () => {
    const homepageButton = document.querySelector('[data-testid="admin-tab-homepage"]');
    if (!homepageButton) return;
    homepageButton.click();
    window.setTimeout(() => {
      window.location.hash = "project-gallery";
      setProjectTabActive(true);
    }, 0);
  };

  const submitHeaderSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    setSearchOpen(false);
    navigate(query ? `/catalog?q=${encodeURIComponent(query)}` : "/catalog");
  };

  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const linkClass = ({ isActive }) =>
    `relative text-[13px] font-medium uppercase tracking-[0.22em] transition-colors pb-1 ${
      isActive
        ? "text-[#F4E6CC] after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-px after:bg-[#D4AF37]"
        : "text-white/65 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      onMouseLeave={() => setNavPreview(null)}
      className={`relative sticky top-0 z-50 w-full backdrop-blur-2xl transition-all duration-300 ${scrolled ? "bg-[#16070f]/90 border-b border-[#BF9972]/20" : "bg-[#16070f]/50"}`}
    >
      {projectTabHost && createPortal(
        <button
          type="button"
          data-testid="admin-tab-project-gallery"
          onClick={openProjectGallery}
          className={`inline-flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.24em] border-b-2 transition-colors ${projectTabActive ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/60 hover:text-white"}`}
        >
          <Images size={14} /> Project Gallery
        </button>,
        projectTabHost
      )}

      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <Link to="/" data-testid="header-brand" className="flex items-center gap-3 whitespace-nowrap">
          <span className="logo-badge inline-flex h-10 w-10 flex-shrink-0">
            <img src="/logo-header.webp" alt="Samrat Glass Emporium" className="w-full h-full object-cover" />
          </span>
          <span className="hidden font-serif text-base tracking-wide leading-tight sm:block md:text-lg">
            <span className="block text-white">Samrat Glass</span>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-[#BF9972]">Emporium</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_ITEMS.map((item, index) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={linkClass}
              data-testid={item.testid}
              onMouseEnter={() => setNavPreview(index)}
              onFocus={() => setNavPreview(index)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
          <div ref={searchMenuRef} className="group relative">
            <button
              type="button"
              aria-label="Search products by text or photo"
              aria-expanded={searchOpen}
              aria-controls="header-product-search-menu"
              data-testid="header-search"
              onClick={() => setSearchOpen((current) => !current)}
              className="group inline-flex h-10 w-10 items-center justify-center gap-2 border border-white/10 text-white/78 transition-[background-color,border-color,color] hover:border-[#D4AF37]/55 hover:bg-[#D4AF37]/[0.08] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#D4AF37] xl:w-auto xl:px-3"
            >
              <span className="photo-search-header-glyph relative">
                <Search size={19} strokeWidth={1.6} aria-hidden="true" />
                <Camera size={10} strokeWidth={1.8} aria-hidden="true" className="absolute -bottom-1 -right-1.5 text-[#D4AF37]" />
              </span>
              <span className="hidden whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.18em] text-white/72 group-hover:text-[#D4AF37] xl:inline">Search</span>
            </button>

            {!searchOpen && (
              <div
                role="tooltip"
                className="premium-search-tooltip pointer-events-none absolute right-0 top-[calc(100%+0.7rem)] z-[85] hidden w-72 translate-y-1 border border-[#D4AF37]/30 bg-[#140910]/[0.98] p-4 text-left opacity-0 shadow-[0_18px_50px_rgba(0,0,0,0.5)] transition-[opacity,transform] duration-200 group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 md:block"
              >
                <div className="text-[9px] font-semibold uppercase tracking-[0.26em] text-[#D4AF37]">Search your way</div>
                <p className="mt-2 text-xs leading-relaxed text-white/72">Type a product name, SKU or category—or upload a room photo or screenshot.</p>
                <p className="mt-2 text-[10px] leading-relaxed text-white/45">Exact matches appear first, followed by similar designs.</p>
              </div>
            )}

            {searchOpen && (
              <div
                id="header-product-search-menu"
                role="dialog"
                aria-label="Search products"
                className="premium-search-menu fixed left-3 right-3 top-[5.25rem] z-[90] overflow-hidden border border-[#D4AF37]/30 bg-[#140910]/[0.98] p-5 shadow-[0_24px_80px_rgba(0,0,0,0.6)] backdrop-blur-xl sm:absolute sm:left-auto sm:right-0 sm:top-[calc(100%+0.75rem)] sm:w-[370px]"
              >
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_92%_8%,rgba(212,175,55,0.14),transparent_38%)]" />
                <div className="relative">
                  <div className="text-[9px] font-medium uppercase tracking-[0.3em] text-[#D4AF37]">Search</div>
                  <h2 className="mt-2 font-serif text-xl text-[#FFF8ED]">How would you like to search?</h2>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">Enter a product name, SKU or category, or use a photo you already have.</p>

                  <form onSubmit={submitHeaderSearch} className="mt-4">
                    <label htmlFor="header-product-search-input" className="sr-only">Search by product name, SKU or type</label>
                    <div className="flex border border-white/15 bg-black/20 focus-within:border-[#D4AF37]/65">
                      <input
                        id="header-product-search-input"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Name, SKU or product type"
                        autoFocus
                        className="min-w-0 flex-1 bg-transparent px-4 py-3 text-sm text-white outline-none placeholder:text-white/35"
                      />
                      <button type="submit" aria-label="Search catalogue" className="flex w-12 items-center justify-center bg-[#D4AF37] text-black transition-colors hover:bg-[#E3C85F]">
                        <ArrowUpRight size={17} aria-hidden="true" />
                      </button>
                    </div>
                  </form>

                  <div className="my-4 flex items-center gap-3" aria-hidden="true">
                    <span className="h-px flex-1 bg-white/10" />
                    <span className="text-[9px] uppercase tracking-[0.2em] text-white/35">or</span>
                    <span className="h-px flex-1 bg-white/10" />
                  </div>

                  <Suspense fallback={null}><CustomerImageSearch variant="menu" onClose={() => setSearchOpen(false)} /></Suspense>
                  <p className="mt-2 text-[10px] leading-relaxed text-white/42">Use a room photo, screenshot or saved image. We’ll show exact matches first, followed by similar options.</p>
                </div>
              </div>
            )}
          </div>
          <Link to="/favorites" aria-label="Favorites" data-testid="header-favorites" className="relative h-10 w-10 flex items-center justify-center text-white/75 hover:text-[#D4AF37] transition-colors">
            <Heart size={20} strokeWidth={1.6} />
            {favorites.length > 0 && (
              <span className="absolute top-0 right-0 text-[10px] bg-[#D4AF37] text-black px-1.5 py-0.5">{favorites.length}</span>
            )}
          </Link>
          <Link to="/cart" aria-label="Cart" data-testid="header-cart" className="relative h-10 w-10 flex items-center justify-center text-white/75 hover:text-[#D4AF37] transition-colors">
            <ShoppingBag size={20} strokeWidth={1.6} />
            {cartCount > 0 && (
              <span data-testid="cart-count" className="absolute top-0 right-0 text-[10px] bg-[#D4AF37] text-black px-1.5 py-0.5">{cartCount}</span>
            )}
          </Link>
          <button
            className="md:hidden h-10 w-10 flex items-center justify-center text-white/75"
            data-testid="header-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {navPreview !== null && NAV_ITEMS[navPreview] && (
        <div
          data-testid="nav-preview-overlay"
          className="absolute left-0 right-0 top-full z-[70] hidden border-y border-[#BF9972]/35 bg-[#12070d] shadow-[0_22px_60px_rgba(0,0,0,0.48)] md:block"
        >
          <div className="pointer-events-none absolute inset-0 opacity-60" style={{ background: "linear-gradient(110deg, rgba(88,27,55,.28), transparent 42%, rgba(212,175,55,.035))" }} />
          <div className="relative mx-auto grid max-w-7xl grid-cols-[1.15fr_.85fr] gap-12 px-6 py-8">
            <div className="min-w-0 pr-4">
              <div className="text-[10px] font-medium uppercase tracking-[0.32em] text-[#D6B07B]">{NAV_ITEMS[navPreview].eyebrow}</div>
              <div className="mt-2 max-w-3xl font-serif text-[30px] leading-tight text-[#FFF8ED]">{NAV_ITEMS[navPreview].title}</div>
              <p className="mt-3 max-w-2xl text-[15px] leading-7 text-white/80">{NAV_ITEMS[navPreview].description}</p>
            </div>
            <div className="grid grid-cols-3 gap-0 self-stretch border-l border-white/10">
              {NAV_ITEMS[navPreview].links.map(([to, label]) => (
                <Link
                  key={`${NAV_ITEMS[navPreview].to}-${to}-${label}`}
                  to={to}
                  className="group flex min-h-[96px] flex-col justify-center border-r border-white/10 px-5 py-3 transition-colors hover:bg-white/[0.035]"
                >
                  <span className="block text-[11px] font-medium uppercase tracking-[0.2em] text-white/85 transition-colors group-hover:text-[#E0C15D]">{label}</span>
                  <ArrowUpRight size={14} className="mt-3 text-[#D4AF37] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="md:hidden border-t border-[#BF9972]/20 px-6 py-6 flex flex-col gap-4 bg-[#16070f]">
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setOpen(false)} className={linkClass}>{item.label}</NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
